import OutlookRestRequest from './request'
import { addBreadcrumb, captureException } from '../../sentry-config'
import { isDesktopMac, isWeb } from '../client'

type RestAttachment = {
  id: string
  name: string
  isInline: boolean
  contentId: string
  contentBytes: string
  Id: string
  Name: string
  IsInline: boolean
  ContentId: string
  ContentBytes: string
}

type RestMessage = {
  subject: string
  body: string
  attachments: RestAttachment[]
}

// The underlying Office mailbox item exposes a varying surface depending on
// host (compose/read, message/appointment); typing it as `any` here matches the
// rest of the office layer which already treats `mailbox.item` opaquely.
type OfficeWithMailbox = typeof Office & {
  context: {
    mailbox: {
      item: any
      convertToRestId: (id: string, version: string) => string
    }
    requirements: { isSetSupported: (set: string, version: string) => boolean }
  }
  MailboxEnums: { RestVersion: { v2_0: string } }
}

export default class OutlookRest {
  office: OfficeWithMailbox
  api: OutlookRestRequest

  constructor(office: OfficeWithMailbox) {
    this.office = office
    this.api = new OutlookRestRequest(office)
  }

  isSupported = (): boolean =>
    this.office.context.requirements.isSetSupported('Mailbox', '1.5') &&
    (isWeb(this.office) || isDesktopMac(this.office))

  isValidResponse = (response: Response | false | undefined): response is Response =>
    !!response &&
    response.ok &&
    !!response.headers.get('content-type')?.includes('application/json')

  getCurrentMessage = async (restId: string): Promise<RestMessage | false> => {
    if (!this.isSupported()) {
      addBreadcrumb({ message: 'Rest: not supported', category: 'office-js' })
      return false
    }

    try {
      const response = await this.api.get(
        `me/messages/${this.office.context.mailbox
          .convertToRestId(restId, this.office.MailboxEnums.RestVersion.v2_0)
          .replaceAll('/', '-')}/?$expand=attachments($select=IsInline)`,
        {}
      )
      if (this.isValidResponse(response)) {
        addBreadcrumb({ message: 'Rest: received a message', category: 'ms-graph-api' })
        const message = await response.json()
        const attachments = await this.fetchInlineAttachments(restId, message.attachments)
        const body = message.body.content

        return {
          subject: message.subject,
          body,
          attachments: attachments.filter(
            (attachment): attachment is RestAttachment =>
              !!attachment && (!attachment.isInline || body.includes(attachment.contentId))
          ),
        }
      }
    } catch (error) {
      captureException(new Error(error.name || 'OOutlookRest#getCurrentMessage'), {
        fingerprint: ['OutlookRest#getCurrentMessage'],
        extra: {
          message: error.message,
          code: error.code,
          traceMessages: error.traceMessages || [],
        },
      })
      return false
    }

    return false
  }

  fetchInlineAttachments = async (
    restId: string,
    attachments: { id: string }[]
  ): Promise<(RestAttachment | false)[]> => {
    return Promise.all(attachments.map(attachment => this.getAttachmentById(restId, attachment.id)))
  }

  getAttachmentById = async (
    restId: string,
    attachmentId: string
  ): Promise<RestAttachment | false> => {
    const response = await this.api.get(
      `me/messages/${this.office.context.mailbox
        .convertToRestId(restId, this.office.MailboxEnums.RestVersion.v2_0)
        .replaceAll('/', '-')}/attachments/${attachmentId}`,
      {}
    )
    if (this.isValidResponse(response)) {
      addBreadcrumb({ message: 'Rest: received attachment', category: 'ms-graph-api' })
      const data = await response.json()
      return {
        ...data,
        Name: data.name,
        IsInline: data.isInline,
        ContentId: data.contentId,
        ContentBytes: data.contentBytes,
        Id: data.id,
      }
    }
    return false
  }
}
